
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import { useEffect } from "react";
import MapOutlinedIcon from '@material-ui/icons/MapOutlined';
import {
  createCurrencyField,
  createDateField,
} from '../temp/componentBuiderTemp';
import { FULL_RECOURSE, insertIf, loanRecourse, PURCHASE, REFINANCE, yesNoOptions, yesNoOptionsBoolean } from "@roc/feature-utils";
import { observer } from "mobx-react";

import { formatCurrency } from "@roc/ui/utils";
import BusinessCenterOutlinedIcon from '@material-ui/icons/BusinessCenterOutlined';
import { RentalPortfolioInformationFormStore } from "../../stores/rentalPortfolio/rentalPortfolioInformationFormStore";
import { FORM_MAX_WIDTH, propertyOwnershipOptions } from "../../utils/constants";
import { RoundedBackgroundIcon } from "../common/roundedBackgroundIcon";
import { CapitalImprovements, HighestPropertyAsIsValue, LowestPropertyAsIsValue, PortfolioAnnualHoaDues, PortfolioAnnualInsurance, PortfolioAnnualTaxes, PortfolioMonthlyMarketRent, AcquisitionPrice, Section8, CashOut, TotalPropertyAsIsValue } from "../fixFlip/propertyInformationFields";
import { PropertyInformationTimeline, TimelineIconAnnualHoaDuesValue, TimelineIconAnnualInsuranceValue, TimelineIconAnnualTaxesValue, TimelineIconMarketValue, TimelineIconMonthlyMarketRentValue, TimelineIconPurchasePrice, TimelineIconRenoSpent, TimelineIconTotalEstimatedAsIsValue } from "../fixFlip/propertyInformationTimeline";
import { ItemDisplay } from "../fixFlip/itemDisplay";
import { createRadioField } from "@roc/ui";


const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    position: 'relative',
    maxWidth: 1300,
    margin: 'auto',
  },
  substractionContainer: {
    display: 'flex',
    width: '90%',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: '20px'
  },
  symbols: {
    color: '#BABEC5',
    fontSize: '60px',
    fontWeight: 300
  },
}));

interface PortfolioDetailsFormProps {
  portfolioInformationFormStore: RentalPortfolioInformationFormStore;
}

export const PortfolioDetailsForm = observer(({ portfolioInformationFormStore }: PortfolioDetailsFormProps) => {
  const classes = useStyles();

  const loanPurpose = portfolioInformationFormStore.form.fields.loanPurpose.value;

  const isPurchase = loanPurpose !== REFINANCE;

  const anyDebt = portfolioInformationFormStore.form.fields.anyDebt.value;

  useEffect(() => {
    if (portfolioInformationFormStore.form.fields.loanRecourse.value !== FULL_RECOURSE) {
      portfolioInformationFormStore.onFieldChange('loanRecourse', FULL_RECOURSE);
    }
  }, [portfolioInformationFormStore]);

  return (
    <Grid
      container
      spacing={2}
      className={classes.container}
      style={{ maxWidth: FORM_MAX_WIDTH, marginBottom: '0.5rem' }}
    >
      <Grid item xs={12}>
        <Grid
          container
          spacing={2}
          justifyContent="flex-start"
          alignItems="center"
        >
          <Grid item>
            <RoundedBackgroundIcon
              Icon={BusinessCenterOutlinedIcon}
              iconColor="#6D6D6D"
            />
          </Grid>
          <Grid item>
            <Typography variant="h4" style={{ color: '#6D6D6D' }}>
              Portfolio Details
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        {createRadioField({
          label: 'Is the property owned by the borrower(s)?',
          fieldName: 'loanPurpose',
          testId: 'loanPurpose',
          store: portfolioInformationFormStore,
          options: propertyOwnershipOptions,
          row: true,
          standaloneLabel: false
        })}
      </Grid>
      <Grid item xs={12} sm={6}>
        <Section8 store={portfolioInformationFormStore}></Section8>
      </Grid>
      <Grid item xs={6}>
        {!isPurchase && createDateField({
          label: 'Last Purchase Date',
          fieldName: 'lastPurchaseDate',
          testId: 'lastPurchaseDate',
          store: portfolioInformationFormStore,
          standaloneLabel: false,
          disableFuture: true,
        })}
      </Grid>
      {!isPurchase && (
        <>
          <Grid item xs={6}>
            {createRadioField({
              label: 'Any Debt on the portfolio?',
              fieldName: 'anyDebt',
              testId: 'anyDebt',
              store: portfolioInformationFormStore,
              options: yesNoOptionsBoolean,
              row: true,
              standaloneLabel: false,
            })}
          </Grid>
          <Grid item xs={6}>
            {anyDebt && createCurrencyField({
              label: "Current Debt on the portfolio",
              fieldName: 'totalDebtPayoff',
              testId: 'totalDebtPayoff',
              store: portfolioInformationFormStore,
              standaloneLabel: false,
              isRequired: true
            })}
          </Grid>
        </>)}
      {anyDebt && !isPurchase && (
        <>
          <Grid item xs={6}>
            <CashOut store={portfolioInformationFormStore} />
          </Grid>
        </>)}
      <Grid item xs={12}>
        <PropertyInformationTimeline
          items={[
            {
              icon: <TimelineIconPurchasePrice />,
              right: isPurchase && (
                <AcquisitionPrice label="What is the Total Purchase Price" store={portfolioInformationFormStore} />
              ),
              left: !isPurchase && (
                <AcquisitionPrice label="What was the Total Purchase Price" store={portfolioInformationFormStore} />
              )
            },
            {
              icon: <TimelineIconMarketValue />,
              right: <HighestPropertyAsIsValue store={portfolioInformationFormStore} />,
              left: <LowestPropertyAsIsValue store={portfolioInformationFormStore} />
            },
            ...insertIf(!isPurchase, [
              {
                icon: <TimelineIconRenoSpent />,
                left: <CapitalImprovements store={portfolioInformationFormStore} />,
              },
            ]),
            {
              icon: <TimelineIconTotalEstimatedAsIsValue />,
              right: <TotalPropertyAsIsValue store={portfolioInformationFormStore} />,
            },
            {
              icon: <TimelineIconMonthlyMarketRentValue />,
              right: <PortfolioMonthlyMarketRent store={portfolioInformationFormStore} />,
              gray: true,
            },
            {
              icon: <TimelineIconAnnualTaxesValue />,
              right: <PortfolioAnnualTaxes store={portfolioInformationFormStore} />,
              gray: true,
            },
            {
              icon: <TimelineIconAnnualInsuranceValue />,
              right: <PortfolioAnnualInsurance store={portfolioInformationFormStore} />,
              gray: true,
            },
            {
              icon: <TimelineIconAnnualHoaDuesValue />,
              right: <PortfolioAnnualHoaDues store={portfolioInformationFormStore} />,
              gray: true,
            },
          ]}
        />
      </Grid>
      <Grid item xs={12}>
        <Box className={classes.substractionContainer}>
          <ItemDisplay bottomLabel label="Total Annual Income" value={`${formatCurrency(portfolioInformationFormStore.totalAnnualIncome)}`}></ItemDisplay>
          <Typography className={classes.symbols} align="center" variant="h4">
            -
          </Typography>
          <ItemDisplay bottomLabel label="Annual Expenses" value={`${formatCurrency(portfolioInformationFormStore.annualExpenses)}`}></ItemDisplay>
          <Typography className={classes.symbols} align="center" variant="h4">
            =
          </Typography>
          <ItemDisplay bottomLabel label="Annual NOI" value={`${formatCurrency(portfolioInformationFormStore.annualNOI)}`}></ItemDisplay>
        </Box>
      </Grid>
    </Grid>
  );
});
